import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  listThread, sendMessage, markThreadRead, getPublicProfile,
} from "@/lib/social";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import { supabase, fmtError } from "@/lib/supabase";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";

function fmtTime(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function MessageThreadPage() {
  const { otherId } = useParams();
  const { user } = useAuth();
  const { refresh } = useRealtime();
  const navigate = useNavigate();
  const [other, setOther] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!user?.id || !otherId) return;
    let alive = true;
    setLoading(true);
    Promise.all([getPublicProfile(otherId), listThread(user.id, otherId)])
      .then(([p, msgs]) => {
        if (!alive) return;
        setOther(p);
        setMessages(msgs);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [user?.id, otherId]);

  // mark recipient's incoming msgs read
  useEffect(() => {
    if (!user?.id || !otherId) return;
    markThreadRead(user.id, otherId).then(() => refresh()).catch(() => {});
  }, [user?.id, otherId, messages.length, refresh]);

  // realtime: subscribe to new messages in the conversation
  useEffect(() => {
    if (!user?.id || !otherId) return;
    const ch = supabase
      .channel(`thread-${user.id}-${otherId}`)
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "messages",
          filter: `sender_id=eq.${otherId}` },
        (payload) => {
          if (payload.new.recipient_id === user.id) {
            setMessages((arr) => [...arr, payload.new]);
          }
        })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user?.id, otherId]);

  // scroll to bottom on new messages
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const onSend = async (e) => {
    e?.preventDefault();
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const m = await sendMessage(user.id, otherId, text);
      if (m) {
        setMessages((arr) => [...arr, m]);
        setBody("");
      }
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setSending(false);
    }
  };

  if (!user) return null;

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)] flex flex-col">
      <div className="max-w-3xl w-full mx-auto px-6 md:px-10 pt-6 pb-3 flex items-center justify-between gap-4 border-b border-ink/15">
        <Link to="/messages" data-testid="thread-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink/60 hover:text-ink transition-colors">
          <ArrowLeft size={14} strokeWidth={2} /> Inbox
        </Link>
        {other ? (
          <Link to={`/u/${other.username || other.id}`} data-testid="thread-other-name"
            className="inline-flex items-center gap-2 font-display text-xl font-semibold text-ink hover:text-ink transition-colors min-w-0">
            <Avatar src={other.avatar_url} name={other.display_name} size="sm" />
            <span className="truncate">{other.display_name}</span>
          </Link>
        ) : <span className="text-ink/40">…</span>}
        <span className="w-12" />
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto px-6 md:px-10 py-6 overflow-y-auto">
        {loading ? (
          <div className="text-ink/50 text-sm uppercase tracking-widest text-center py-12">Loading…</div>
        ) : messages.length === 0 ? (
          <div className="text-ink/55 text-sm text-center py-12">Say hi.</div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="thread-messages">
            {messages.map((m) => {
              const mine = m.sender_id === user.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[78%] px-4 py-2.5 ${mine ? "bg-neon text-ink" : "border border-ink/15 text-ink bg-paper"}`}>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{m.content}</p>
                    <p className={`text-[10px] uppercase tracking-widest mt-1 ${mine ? "text-ink/50" : "text-ink/40"}`}>
                      {fmtTime(m.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        )}
      </div>

      <form onSubmit={onSend} data-testid="thread-form"
        className="max-w-3xl w-full mx-auto px-6 md:px-10 py-4 border-t border-ink/15 flex items-center gap-3 bg-paper/95 backdrop-blur sticky bottom-0">
        <input type="text" required value={body} onChange={(e) => setBody(e.target.value)}
          data-testid="thread-input" placeholder="Write a message…"
          className="flex-1 bg-transparent border border-ink/15 focus:border-neon text-ink px-4 py-3 outline-none text-sm transition-colors placeholder:text-ink/40" />
        <button type="submit" disabled={sending || !body.trim()} data-testid="thread-send"
          className="bg-neon text-ink px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-paper transition-colors disabled:opacity-60 inline-flex items-center gap-2">
          <Send size={14} strokeWidth={2.5} /> Send
        </button>
      </form>
    </div>
  );
}
